import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { t } from "i18next";
import LivingMap, { LMFeature } from "@livingmap/core-mapping";
import { Dialog } from "@livingmap/hippo";
import classNames from "classnames";

import Map from "@components/Map/Map";
import Search from "@components/Search/Search";
import { PLUGIN_IDS } from "@components/Map/plugins/types";
import InteractionPlugin from "@components/Map/plugins/interaction-control";
import FloorControl from "@components/Map/plugins/floor-control";
import UnsavedChanges from "../../Modal/UnsavedChanges";

import { useGetMapByIdQuery, Floors } from "@redux/services/mms";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import {
  setFeatureID,
  setHasUnsavedChanges,
} from "@redux/slices/applicationSlice";
import { SearchedFeatureData } from "@redux/services/types";
import { useSearch } from "@hooks";
import { createLMFeature, getFloorByID } from "@utils";

import styles from "./MapView.module.css";
import { useSetSideSheetInstances } from "../../FeaturesView";

const MapView = () => {
  const dispatch = useAppDispatch();
  const { setMapInstance: setContextMapInstance } = useSetSideSheetInstances();

  const [mapInstance, setMapInstance] = useState<LivingMap | null>(null);
  const interactionControlInstance = useRef<InteractionPlugin | null>(null);
  const floorControlInstance = useRef<FloorControl | null>(null);
  const [isUnsavedChangesModalActive, setUnsavedChangesModalActive] =
    useState(false);
  const [localFeature, setLocalFeature] = useState<LMFeature | null>(null);

  const { mapID, defaultLanguage, hasUnsavedChanges } = useAppSelector(
    (state) => state.application,
  );

  // on initial mount, ensure no feature is selected so the EditFeatureSidebar is closed
  useEffect(() => {
    dispatch(setFeatureID(null));
    mapInstance?.getMapboxMap().resize();
  }, [dispatch, mapInstance]);

  if (!mapID) throw new Error("mapID could not be found in the redux state");

  const { data: mapData } = useGetMapByIdQuery(mapID);

  const {
    searchIsActive,
    handleChange,
    handleKeyDown,
    handleClearSearch,
    searchInputValue,
    searchResults,
    isFetching,
  } = useSearch(mapInstance);

  const handleMapFeatureSelect = useCallback(
    (feature: LMFeature) => {
      if (hasUnsavedChanges) {
        setUnsavedChangesModalActive(true);
        setLocalFeature(feature);
        interactionControlInstance.current?.setCancelSelectFeature(true);
        dispatch(setHasUnsavedChanges(false));
        return;
      }

      interactionControlInstance.current?.setCancelSelectFeature(false);

      setLocalFeature(null);
      dispatch(setFeatureID(feature.getMapboxFeature().properties!.lm_id));
    },
    [dispatch, hasUnsavedChanges],
  );

  const handleSearchFeatureSelect = (feature: SearchedFeatureData) => {
    if (hasUnsavedChanges) {
      setUnsavedChangesModalActive(true);
      setLocalFeature(createLMFeature(feature, defaultLanguage));
      return;
    }

    setLocalFeature(null);
    dispatch(setFeatureID(feature.properties.lm_id));

    if (mapInstance && interactionControlInstance.current) {
      // Extract feature coordinates
      const featureCoordinates = [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1],
      ] as [number, number];

      // Change floor first if needed
      const featureFloor = getFloorByID(
        mappedFloorData as Floors,
        feature.properties.floor_id,
      );

      if ("id" in featureFloor) {
        floorControlInstance.current?.setActiveFloor(featureFloor);
      }

      // Pan, zoom, and center on the feature location
      mapInstance.getMapboxMap().easeTo({
        center: featureCoordinates,
        zoom: 18, // Zoom close enough to see individual features/pins
        duration: 500,
      });

      // Create and select the feature immediately using createLMFeature
      const lmFeature = createLMFeature(feature, defaultLanguage);
      interactionControlInstance.current.selectFeature(lmFeature);
    } else {
      console.error(
        "handleSearchFeatureSelect - mapInstance or interactionControlInstance is null",
      );
    }
  };

  const handleConfirmUnsavedChanges = () => {
    interactionControlInstance.current?.setCancelSelectFeature(false);

    if (localFeature) {
      dispatch(setFeatureID(localFeature.getLmId()));
      interactionControlInstance.current?.selectFeature(localFeature);
    }

    setUnsavedChangesModalActive(false);
  };

  const handleCancelUnsavedChanges = () => {
    setUnsavedChangesModalActive(false);
    dispatch(setHasUnsavedChanges(true));
  };

  // Re-subscribe the handleMapFeatureSelect function to the interactionControlInstance if the "hasUnsavedChanges" state changes, since the subscribeToFeatureSelect function creates a closure over the "hasUnsavedChanges" variable
  useEffect(() => {
    interactionControlInstance.current?.subscribeToFeatureSelect(
      handleMapFeatureSelect,
    );
  }, [hasUnsavedChanges, handleMapFeatureSelect]);

  const mappedFloorData =
    mapData &&
    Object.keys(mapData.floors.available)
      .reverse()
      .reduce((finalFloors, floorId) => {
        const floorObj = mapData.floors.available[floorId];
        const floorName = floorObj.name.find(
          (floor) => floor.lang === defaultLanguage,
        );

        return {
          ...finalFloors,
          [floorId]: {
            ...floorObj,
            name: floorName?.text,
          },
        };
      }, {});

  // clear the search bar whenever the map changes
  useEffect(() => {
    handleClearSearch();
    if (mapInstance) {
      interactionControlInstance.current =
        mapInstance.getPluginById<InteractionPlugin>(PLUGIN_IDS.INTERACTION);
      floorControlInstance.current = mapInstance.getPluginById<FloorControl>(
        PLUGIN_IDS.FLOOR,
      );
    }
  }, [mapID, mapInstance, handleClearSearch]);

  // Select the correct stylesheet based on the language selected by the user
  const mapStylesheet = useMemo(() => {
    const defaultStylesheet = mapData?.stylesheets.available.find(
      (sheet) => sheet.id === mapData?.stylesheets.default,
    )?.url;

    return `${defaultStylesheet}?lang=${defaultLanguage}`;
  }, [
    defaultLanguage,
    mapData?.stylesheets.available,
    mapData?.stylesheets.default,
  ]);

  return (
    <div className={styles.container} data-qa="features-map-view">
      <div className={styles.mapContainer}>
        <div
          className={classNames({
            [styles.searchWrapper]: true,
            [styles.fullHeight]: searchIsActive,
          })}
        >
          <Search
            dataQA="search-container"
            isFetching={isFetching}
            searchIsActive={searchIsActive}
            searchValue={searchInputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClearClick={handleClearSearch}
            results={searchResults}
            placeholder={t("views.map_view.search_placeholder")}
            onResultClick={handleSearchFeatureSelect}
          />
        </div>
        {mapData && mappedFloorData && (
          <div style={{ height: "100%", width: "100%" }}>
            <Map
              dataQA="map-component"
              mapID={mapData.id}
              accessToken={mapData.access_token}
              bearing={mapData.bearing}
              zoom={mapData.zoom.default}
              maxZoom={mapData.zoom.maximum}
              minZoom={mapData.zoom.minimum}
              center={[mapData.center.longitude, mapData.center.latitude]}
              extent={[
                mapData.extents.bottom_left.longitude,
                mapData.extents.bottom_left.latitude,
                mapData.extents.top_right.longitude,
                mapData.extents.top_right.latitude,
              ]}
              mapStyle={mapStylesheet}
              floors={mappedFloorData}
              floor={mapData.floors.default}
              onFeatureSelect={handleMapFeatureSelect}
              onMapReady={(map) => {
                setMapInstance(map);
                setContextMapInstance && setContextMapInstance(map);
              }}
            />
          </div>
        )}
      </div>
      <Dialog
        dataQA="unsaved-changes-dialog"
        isOpen={isUnsavedChangesModalActive}
        onClose={handleCancelUnsavedChanges}
        footer={false}
      >
        <UnsavedChanges
          onCancel={handleCancelUnsavedChanges}
          onConfirm={handleConfirmUnsavedChanges}
        />
      </Dialog>
    </div>
  );
};

export default MapView;
